import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Plus } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { ExistenciaPorUbicacion, Ubicacion } from '@/api/tienda.api';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { useToast } from '@/hooks/useToast';
import { numero, fecha } from '@/lib/formato-tienda';
import { ConfirmarTraslado } from '@/components/tienda/ConfirmarTraslado';

/**
 * ALMACÉN Y TIENDAS — dónde está cada pieza y quién la movió.
 *
 * La mercancía que llega cae en el almacén. De ahí sale a una tienda o directo a una venta,
 * y cada salida lleva quién la sacó y a dónde va. El total del negocio lo sigue llevando
 * Inventario; esta pantalla solo dice en qué lugar está.
 */

const VENTA_DIRECTA = '__venta_directa__';
const etiquetaDe = (e: ExistenciaPorUbicacion) => `${e.producto.name} · ${e.label}`;

export default function Almacen() {
  const [mover, setMover] = useState<{ varianteId?: string } | null>(null);
  const [contar, setContar] = useState<ExistenciaPorUbicacion | null>(null);
  const [nuevaTienda, setNuevaTienda] = useState(false);
  // SUPERFILTRO (Josué, 14-oct): buscar, en qué lugar hay y si queda o no. En la URL.
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { buscar: '', lugar: '', existencia: '' },
    { existencia: ['con', 'agotada'] },
  );
  const buscar = filtros.buscar;

  const ubicaciones = useQuery({ queryKey: ['inventario', 'ubicaciones'], queryFn: tienda.listarUbicaciones });
  const existencias = useQuery({ queryKey: ['inventario', 'por-ubicacion'], queryFn: tienda.existenciasPorUbicacion });
  const traslados = useQuery({ queryKey: ['inventario', 'traslados'], queryFn: tienda.listarTraslados });
  // Lo que salió hacia una tienda y nadie de esa tienda ha dicho que llegó.
  const porConfirmar = (traslados.data ?? []).filter((t) => t.destino && !t.recibidoAt).length;

  const lugares = useMemo(() => ubicaciones.data ?? [], [ubicaciones.data]);
  const tiendas = lugares.filter((u) => u.tipo === 'TIENDA');
  const hayEn = (e: ExistenciaPorUbicacion, u: Ubicacion) => (u.tipo === 'ALMACEN' ? e.enAlmacen : (e.porTienda[u.id] ?? 0));
  const filas = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const todas = existencias.data ?? [];
    const lugar = lugares.find((u) => u.id === filtros.lugar);
    return todas.filter(
      (e) =>
        (!q || `${etiquetaDe(e)} ${e.sku ?? ''} ${e.producto.sku ?? ''}`.toLowerCase().includes(q)) &&
        (!lugar || (lugar.tipo === 'ALMACEN' ? e.enAlmacen : (e.porTienda[lugar.id] ?? 0)) > 0) &&
        (filtros.existencia !== 'con' || e.total > 0) &&
        (filtros.existencia !== 'agotada' || e.total <= 0),
    );
  }, [existencias.data, lugares, buscar, filtros.lugar, filtros.existencia]);
  const todasLasPiezas = existencias.data ?? [];
  const facetas: Faceta[] = [
    {
      clave: 'lugar',
      titulo: 'Hay en',
      todas: 'Cualquier lugar',
      opciones: lugares.map((u) => ({ valor: u.id, etiqueta: u.nombre, cuenta: todasLasPiezas.filter((e) => hayEn(e, u) > 0).length })),
    },
    {
      clave: 'existencia',
      titulo: 'Existencia',
      todas: 'Todas',
      opciones: [
        { valor: 'con', etiqueta: 'Con existencia', cuenta: todasLasPiezas.filter((e) => e.total > 0).length },
        { valor: 'agotada', etiqueta: 'Agotadas', cuenta: todasLasPiezas.filter((e) => e.total <= 0).length },
      ],
    },
  ];

  const piezasEn = (u: Ubicacion) =>
    (existencias.data ?? []).reduce((a, e) => a + (u.tipo === 'ALMACEN' ? e.enAlmacen : (e.porTienda[u.id] ?? 0)), 0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <ModuleHeader
        titulo="Almacén y tiendas"
        subtitulo="Dónde está cada pieza. «Contar» corrige lo que hay en un lugar; cada salida lleva quién la sacó y a dónde va."
        acciones={
          <>
            <Button variant="outline" onClick={() => setNuevaTienda(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Nueva tienda
            </Button>
            <Button onClick={() => setMover({})} disabled={(existencias.data ?? []).length === 0}>
              Registrar salida
            </Button>
          </>
        }
        kpis={lugares.map((u) => (
          <Kpi
            key={u.id}
            titulo={u.nombre}
            valor={numero(piezasEn(u))}
            pie={u.tipo === 'ALMACEN' ? 'piezas en el almacén' : 'piezas en la tienda'}
          />
        ))}
      />

      <AgenteOtrasTiendas />

      <section className="rounded-2xl border border-surface-border bg-surface">
        <div className="space-y-3 border-b border-surface-border p-4">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Existencia por lugar</h2>
          <Superfiltro
            buscar={buscar}
            onBuscar={(v) => poner('buscar', v)}
            placeholder="Busca una pieza…"
            facetas={facetas}
            valores={filtros}
            onCambiar={ponerVarios}
            onLimpiar={() => ponerVarios(sinFiltros(facetas))}
            resultados={existencias.isLoading ? undefined : textoDeResultados(filas.length, todasLasPiezas.length, ['pieza', 'piezas'])}
          />
        </div>
        {filas.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            {existencias.isLoading
              ? 'Cargando…'
              : todasLasPiezas.length > 0
                ? 'Nada con esos filtros.'
                : 'Todavía no hay productos. Cárgalos en Productos y aquí verás en qué lugar está cada pieza.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 font-medium">Pieza</th>
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                  <th className="px-4 py-2 text-right font-medium">Almacén</th>
                  {tiendas.map((t) => (
                    <th key={t.id} className="px-4 py-2 text-right font-medium">
                      {t.nombre}
                    </th>
                  ))}
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {filas.map((e) => (
                  <tr key={e.id} className="border-t border-surface-border">
                    <td className="px-4 py-2 text-slate-900 dark:text-slate-100">{etiquetaDe(e)}</td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums">{numero(e.total)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{numero(e.enAlmacen)}</td>
                    {tiendas.map((t) => (
                      <td key={t.id} className="px-4 py-2 text-right tabular-nums">
                        {numero(e.porTienda[t.id] ?? 0)}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setContar(e)}>
                        Contar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setMover({ varianteId: e.id })}>
                        Mover
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-surface-border bg-surface">
        <h2 className="border-b border-surface-border p-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
          Salidas y movimientos
          {porConfirmar > 0 && (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {porConfirmar} por confirmar en tienda
            </span>
          )}
        </h2>
        {(traslados.data ?? []).length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            Todavía no hay salidas. Cuando alguien saque mercancía del almacén, queda aquí con su nombre y su destino.
          </p>
        ) : (
          <ul className="divide-y divide-surface-border">
            {(traslados.data ?? []).map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 p-4 text-sm">
                <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">{t.cantidad} ×</span>
                <span className="text-slate-900 dark:text-slate-100">
                  {t.productVariant.product.name} · {t.productVariant.label}
                </span>
                <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                  {t.origen.nombre}
                  <ArrowRight className="h-3.5 w-3.5" />
                  {t.destino?.nombre ?? 'Venta directa'}
                </span>
                <span className="text-slate-500">
                  la sacó {t.sacadoPor} · {fecha(t.createdAt)}
                  {t.nota ? ` · ${t.nota}` : ''}
                </span>
                {t.destino && <ConfirmarTraslado traslado={t} />}
              </li>
            ))}
          </ul>
        )}
      </section>

      {mover && (
        <ModalDeSalida
          lugares={lugares}
          existencias={existencias.data ?? []}
          varianteInicial={mover.varianteId}
          onCerrar={() => setMover(null)}
        />
      )}
      {nuevaTienda && <ModalDeTienda onCerrar={() => setNuevaTienda(false)} />}
      {contar && <ModalDeConteo pieza={contar} lugares={lugares} onCerrar={() => setContar(null)} />}
    </div>
  );
}

/** El asistente de cada sucursal mira su tienda; esto dice si ofrece lo que hay en otro lugar. */
function AgenteOtrasTiendas() {
  const qc = useQueryClient();
  const toast = useToast();
  const estado = useQuery({ queryKey: ['inventario', 'agente-otras-tiendas'], queryFn: tienda.agenteOtrasTiendas });
  const cambiar = useMutation({
    mutationFn: (encendido: boolean) => tienda.ponerAgenteOtrasTiendas(encendido),
    onSuccess: (r) => {
      qc.setQueryData(['inventario', 'agente-otras-tiendas'], r);
      toast.success(r.encendido ? 'El asistente ofrecerá lo de otras tiendas' : 'El asistente solo ofrece lo de su tienda');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });
  if (estado.isError) {
    return (
      <p className="rounded-2xl border border-surface-border bg-surface p-4 text-sm text-red-600">
        No se pudo leer si el asistente ofrece lo de otras tiendas. Recarga la página.
      </p>
    );
  }
  if (!estado.data) return null;
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-surface-border bg-surface p-4 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={estado.data.encendido}
        disabled={cambiar.isPending}
        onChange={(e) => cambiar.mutate(e.target.checked)}
      />
      <span>
        <span className="font-medium text-slate-900 dark:text-slate-100">
          El asistente de cada sucursal ofrece lo que hay en otra tienda o en el almacén
        </span>
        <span className="block text-slate-500">
          Mira primero la tienda de su WhatsApp. Si ahí no hay, dice dónde sí hay; el envío o el
          traslado los confirma una persona. Apagado, solo ofrece lo de su tienda.
        </span>
      </span>
    </label>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">{etiqueta}</label>
      {children}
    </div>
  );
}

function ModalDeSalida({
  lugares,
  existencias,
  varianteInicial,
  onCerrar,
}: {
  lugares: Ubicacion[];
  existencias: ExistenciaPorUbicacion[];
  varianteInicial?: string;
  onCerrar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const almacen = lugares.find((u) => u.tipo === 'ALMACEN');
  const [varianteId, setVarianteId] = useState(varianteInicial ?? existencias[0]?.id ?? '');
  const [origenId, setOrigenId] = useState(almacen?.id ?? '');
  const [destino, setDestino] = useState(VENTA_DIRECTA);
  const [cantidad, setCantidad] = useState('');
  const [sacadoPor, setSacadoPor] = useState('');
  const [nota, setNota] = useState('');

  const pieza = existencias.find((e) => e.id === varianteId);
  const origen = lugares.find((u) => u.id === origenId);
  const hay = pieza && origen ? (origen.tipo === 'ALMACEN' ? pieza.enAlmacen : (pieza.porTienda[origen.id] ?? 0)) : 0;
  const n = Number(cantidad);
  const listo = Boolean(pieza && origen) && Number.isInteger(n) && n > 0 && n <= hay && sacadoPor.trim() !== '' && destino !== origenId;

  const guardar = useMutation({
    mutationFn: () =>
      tienda.trasladar({
        productVariantId: varianteId,
        cantidad: n,
        origenId,
        destinoId: destino === VENTA_DIRECTA ? null : destino,
        sacadoPor: sacadoPor.trim(),
        nota: nota.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Salida registrada');
      void qc.invalidateQueries({ queryKey: ['inventario'] });
      void qc.invalidateQueries({ queryKey: ['productos'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo registrar'),
  });

  return (
    <Modal open onClose={onCerrar} title="Registrar salida">
      <div className="space-y-4">
        <Campo etiqueta="Qué pieza">
          <Select value={varianteId} onChange={(e) => setVarianteId(e.target.value)}>
            {existencias.map((e) => (
              <option key={e.id} value={e.id}>
                {etiquetaDe(e)}
              </option>
            ))}
          </Select>
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="De dónde sale">
            <Select value={origenId} onChange={(e) => setOrigenId(e.target.value)}>
              {lugares.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="A dónde va">
            <Select value={destino} onChange={(e) => setDestino(e.target.value)}>
              <option value={VENTA_DIRECTA}>Venta directa (se la lleva el cliente)</option>
              {lugares
                .filter((u) => u.id !== origenId)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre}
                  </option>
                ))}
            </Select>
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Cantidad">
            <Input type="number" min={1} value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder="ej. 3" />
            <p className={`mt-1 text-xs ${n > hay ? 'text-red-600' : 'text-slate-500'}`}>
              En {origen?.nombre ?? 'ese lugar'} hay {numero(hay)}.
            </p>
          </Campo>
          <Campo etiqueta="Quién la sacó">
            <Input value={sacadoPor} onChange={(e) => setSacadoPor(e.target.value)} placeholder="Nombre de la persona" />
          </Campo>
        </div>

        <Campo etiqueta="Nota (opcional)">
          <Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Para qué cliente, quién la recibe…" />
        </Campo>

        {destino === VENTA_DIRECTA && (
          <p className="text-xs text-slate-500">
            La venta directa descuenta la existencia del negocio y no deja papel. Si la vas a registrar
            en Ventas (con su cliente, su cobro o su factura), no la saques aquí: la venta ya dice de
            qué lugar sale y lo descuenta ella.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={!listo || guardar.isPending}>
            {guardar.isPending ? 'Registrando…' : 'Registrar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * El conteo: se escribe cuántas hay DE VERDAD en cada lugar. Lo que cambió queda en el libro de
 * Inventario como ajuste («Conteo en Tienda Santiago: había 3, hay 5»), y el total del negocio
 * sube o baja por esa diferencia. Para pasar piezas de un lugar a otro está «Mover».
 */
function ModalDeConteo({
  pieza,
  lugares,
  onCerrar,
}: {
  pieza: ExistenciaPorUbicacion;
  lugares: Ubicacion[];
  onCerrar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const habia = (u: Ubicacion) => (u.tipo === 'ALMACEN' ? pieza.enAlmacen : (pieza.porTienda[u.id] ?? 0));
  // Un almacén en negativo (una salida del libro que no miró las tiendas) empieza vacío: solo se
  // manda lo que alguien contó de verdad.
  const [valores, setValores] = useState<Record<string, string>>(() =>
    Object.fromEntries(lugares.map((u) => [u.id, habia(u) < 0 ? '' : String(habia(u))])),
  );
  const [nota, setNota] = useState('');

  const cambiados = lugares.filter((u) => valores[u.id] !== '' && Number(valores[u.id]) !== habia(u));
  const validos = lugares.every((u) => {
    const n = Number(valores[u.id]);
    return valores[u.id] === '' || (Number.isInteger(n) && n >= 0);
  });

  const guardar = useMutation({
    mutationFn: () =>
      tienda.contarEnLugares({
        productVariantId: pieza.id,
        cantidades: Object.fromEntries(cambiados.map((u) => [u.id, Number(valores[u.id])])),
        nota: nota.trim() || null,
      }),
    onSuccess: (r) => {
      toast.success(r.cambios.length === 1 ? 'Conteo guardado en 1 lugar' : `Conteo guardado en ${r.cambios.length} lugares`);
      void qc.invalidateQueries({ queryKey: ['inventario'] });
      void qc.invalidateQueries({ queryKey: ['productos'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar el conteo'),
  });

  return (
    <Modal open onClose={onCerrar} title={`Contar · ${etiquetaDe(pieza)}`}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Escribe cuántas hay de verdad en cada lugar. Lo que cambie queda en el libro de Inventario
          con lo que había y lo que hay.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {lugares.map((u) => (
            <Campo key={u.id} etiqueta={u.nombre}>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                value={valores[u.id] ?? ''}
                onChange={(e) => setValores((v) => ({ ...v, [u.id]: e.target.value }))}
                aria-label={`Piezas en ${u.nombre}`}
              />
              <p className="mt-1 text-xs text-slate-500">El sistema dice {numero(habia(u))}.</p>
            </Campo>
          ))}
        </div>
        <Campo etiqueta="Nota (opcional)">
          <Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Quién contó, qué pasó…" />
        </Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={!validos || cambiados.length === 0 || guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar conteo'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ModalDeTienda({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const crear = useMutation({
    mutationFn: () => tienda.crearUbicacion(nombre.trim()),
    onSuccess: () => {
      toast.success('Tienda creada');
      void qc.invalidateQueries({ queryKey: ['inventario'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo crear'),
  });
  return (
    <Modal open onClose={onCerrar} title="Nueva tienda" size="sm">
      <div className="space-y-4">
        <Campo etiqueta="Nombre">
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Tienda …" />
        </Campo>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={!nombre.trim() || crear.isPending}>
            Crear
          </Button>
        </div>
      </div>
    </Modal>
  );
}
